import React, { useCallback, useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import classNames from 'classnames'
import { Button } from '../../../components/Button/Button'
import { firstApiErrorMessage } from '../../../models/ApiErrors'
import { DOCS_LINKS } from '../constants'
import { Modal } from '../../../components/Modal/Modal'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import { type OAuth2App } from '../../../store/auth/auth'
import { useDeleteDataSourceMutation, useListAppDataSourcesQuery, useVerifyDataSourceMutation } from '../../../store/integrations/externalDataSources'
import { type DataSourceStatus, type ExternalDataSource } from '../../../models/ExternalDataSource'
import { formatFriendlyDateTime } from '../../../common/dateUtils'
import { EditDataSourceModal } from './EditDataSourceModal'
import { RegisterDataSourceModal } from './RegisterDataSourceModal'
import { TestDataSourceModal } from './TestDataSourceModal'
import styles from './ManageAppDataSourcesTab.module.scss'

interface ManageAppDataSourcesTabProps {
  oAuth2App: OAuth2App
}

const STATUS_LABELS: Record<DataSourceStatus, string> = {
  pending: 'Pending',
  active: 'Active',
}

const StatusBadge: React.FC<{ status: DataSourceStatus }> = ({ status }) => (
  <span
    className={classNames(
      'd-inline-flex align-items-center px-2 py-1 rounded-3 paragraph-small',
      status === 'active' ? 'zocks-bg-color-success-background zocks-text-success' : 'zocks-bg-color-warning zocks-text-warning-text',
    )}
  >
    {STATUS_LABELS[status]}
  </span>
)

const ALL_DATA_SOURCES = { page: 0, size: 200 }

const HEADER_CELL = 'px-3 py-2 paragraph-extra-small fw-bold zocks-text-gray-600 text-uppercase'

function useModalTarget<T>() {
  const [target, setTarget] = useState<T | null>(null)
  const [open, setOpen] = useState<boolean>(false)

  const show = useCallback((value: T) => {
    setTarget(value)
    setOpen(true)
  }, [])
  const hide = useCallback(() => setOpen(false), [])
  const release = useCallback(() => setTarget(null), [])

  return useMemo(() => ({ target, open, show, hide, release }), [target, open, show, hide, release])
}

export const ManageAppDataSourcesTab: React.FC<ManageAppDataSourcesTabProps> = ({ oAuth2App }) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const { clientId } = oAuth2App

  const { data, isLoading, isError } = useListAppDataSourcesQuery({ accountId, clientId, pageRequest: ALL_DATA_SOURCES })
  const [verifyDataSource] = useVerifyDataSourceMutation()
  const [deleteDataSource, { isLoading: isDeleting }] = useDeleteDataSourceMutation()

  const [showRegister, setShowRegister] = useState<boolean>(false)
  const testing = useModalTarget<ExternalDataSource>()
  const editing = useModalTarget<ExternalDataSource>()
  const deleting = useModalTarget<ExternalDataSource>()
  const [verifyingId, setVerifyingId] = useState<string | null>(null)

  const handleVerify = useCallback(
    async (dataSource: ExternalDataSource) => {
      setVerifyingId(dataSource.id)
      try {
        const { data: result } = await verifyDataSource({ accountId, clientId, dataSourceId: dataSource.id }).unwrap()
        dispatch(
          result.verified
            ? addToast({
                variant: 'success',
                title: 'Ownership verified',
                messages: ['Now run a test — a passing test is what activates the data source.'],
              })
            : addToast({
                variant: 'error',
                title: 'Verification failed',
                messages: [result.error ?? 'The endpoint did not return the expected challenge response.'],
              }),
        )
      } catch (error) {
        dispatch(addToast({ variant: 'error', title: 'Verification failed', messages: [firstApiErrorMessage(error) ?? 'Could not reach the endpoint.'] }))
      } finally {
        setVerifyingId(null)
      }
    },
    [verifyDataSource, accountId, clientId, dispatch],
  )

  const handleConfirmDelete = useCallback(async () => {
    if (!deleting.target) {
      return
    }
    try {
      await deleteDataSource({ accountId, clientId, dataSourceId: deleting.target.id }).unwrap()
      dispatch(addToast({ variant: 'success', title: 'Data source deleted', messages: [] }))
      deleting.hide()
    } catch (error) {
      dispatch(addToast({ variant: 'error', title: 'Could not delete the data source', messages: [firstApiErrorMessage(error) ?? 'Please try again.'] }))
    }
  }, [deleting, deleteDataSource, accountId, clientId, dispatch])

  const dataSources = data?.data ?? []
  const showHeaderRegister = dataSources.length > 0 || isError

  return (
    <div>
      <div className='d-flex align-items-center zocks-gap-2 mb-4'>
        <div className='flex-grow-1'>
          <h5 className='fw-bold mb-1'>Data sources</h5>
          <p className='paragraph-small zocks-text-gray-600 mb-0 col-xl-8'>
            Zocks calls these sources to pull data about a contact during an AI run. Every account that enables this app gets these with it.{' '}
            {DOCS_LINKS.overview ? (
              <a href={DOCS_LINKS.overview} target='_blank' rel='noreferrer' className='button-link-medium zocks-text-primary'>
                How they work
              </a>
            ) : null}
          </p>
        </div>
        {showHeaderRegister ? (
          <Button variant='primary' onClick={() => setShowRegister(true)} className='flex-shrink-0'>
            Register
          </Button>
        ) : null}
      </div>

      {isLoading ? (
        <div className='d-flex justify-content-center py-5'>
          <Spinner />
        </div>
      ) : null}

      {isError ? (
        <div className='p-3 rounded-2 zocks-bg-color-danger-background zocks-border-color-danger'>
          <p className='paragraph-small mb-0'>Could not load the data sources for this app.</p>
        </div>
      ) : null}

      {!isLoading && !isError && dataSources.length === 0 ? (
        <div className='d-flex flex-column align-items-center text-center p-5 rounded-3 zocks-bg-color-gray-100 zocks-border-color-gray-300'>
          <h6 className='fw-bold mb-1'>No data sources yet</h6>
          <p className='paragraph-small zocks-text-gray-600 mb-3 col-lg-8'>
            Register the HTTPS endpoint Zocks should call. You&apos;ll get a signing secret once, then prove the endpoint is yours and that it answers the
            contract.
          </p>
          <Button variant='primary' onClick={() => setShowRegister(true)}>
            Register a data source
          </Button>
        </div>
      ) : null}

      {dataSources.length > 0 ? (
        <>
          <div className='rounded-3 zocks-border-color-gray-300 overflow-hidden'>
            <table className='w-100'>
              <thead>
                <tr className='zocks-bg-color-gray-100'>
                  <th className={HEADER_CELL}>Data source</th>
                  <th className={HEADER_CELL}>Data types</th>
                  <th className={classNames(HEADER_CELL, styles.shrink)}>Status</th>
                  <th className={classNames(HEADER_CELL, 'text-end', styles.shrink)}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {dataSources.map((dataSource, index) => (
                  <tr key={dataSource.id} className={classNames('zocks-bg-color-white', index < dataSources.length - 1 ? 'border-bottom' : null)}>
                    <td className='px-3 py-3 align-middle'>
                      <div className='paragraph-medium fw-bold'>{dataSource.name}</div>
                      {dataSource.description ? <div className='paragraph-small zocks-text-gray-700'>{dataSource.description}</div> : null}
                      <div className='paragraph-small zocks-text-gray-600 text-break'>{dataSource.callbackUrl}</div>
                      <div className='paragraph-extra-small zocks-text-gray-600'>
                        {dataSource.verifiedAt
                          ? `Verified ${formatFriendlyDateTime(new Date(dataSource.verifiedAt))}`
                          : 'Not verified — Zocks will not pull from this yet'}
                      </div>
                      {dataSource.lastError ? (
                        <div className='d-inline-flex align-items-center zocks-gap-0.375 mt-1 px-2 py-1 rounded-1 zocks-bg-color-gray-100'>
                          <i className='bi bi-info-circle zocks-text-gray-600 paragraph-extra-small' />
                          <span className='paragraph-extra-small zocks-text-gray-600'>
                            Last failure: {dataSource.lastError}
                            {dataSource.lastErrorAt ? ` · ${formatFriendlyDateTime(new Date(dataSource.lastErrorAt))}` : null}
                          </span>
                        </div>
                      ) : null}
                    </td>

                    <td className={classNames('px-3 py-3 align-middle', styles.typesCell)}>
                      <div className='d-flex flex-wrap zocks-gap-0.25'>
                        {dataSource.dataTypes.map((dataType) => (
                          <span key={dataType} className='px-2 py-1 rounded-1 zocks-bg-color-gray-200 zocks-text-gray-700 paragraph-extra-small'>
                            {dataType}
                          </span>
                        ))}
                      </div>
                    </td>

                    <td className={classNames('px-3 py-3 align-middle', styles.shrink)}>
                      <StatusBadge status={dataSource.status} />
                    </td>

                    <td className={classNames('px-3 py-3 align-middle', styles.shrink)}>
                      <div className='d-flex flex-nowrap justify-content-end zocks-gap-0.5'>
                        {dataSource.verifiedAt ? null : (
                          <Button
                            variant='lightPurple'
                            onClick={() => handleVerify(dataSource)}
                            disabled={verifyingId === dataSource.id}
                            aria-busy={verifyingId === dataSource.id}
                            aria-label='Verify'
                            className='position-relative'
                          >
                            <span className={classNames({ invisible: verifyingId === dataSource.id })}>Verify</span>
                            {verifyingId === dataSource.id ? (
                              <span className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'>
                                <Spinner size='sm' />
                              </span>
                            ) : null}
                          </Button>
                        )}
                        <Button variant='lightPurple' onClick={() => testing.show(dataSource)}>
                          Test
                        </Button>
                        <Button variant='darkGhost' onClick={() => editing.show(dataSource)} aria-label={`Edit ${dataSource.name}`}>
                          <i className='bi bi-pencil' />
                        </Button>
                        <Button variant='dangerGhost' onClick={() => deleting.show(dataSource)} aria-label={`Delete ${dataSource.name}`}>
                          <i className='bi bi-trash' />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className='paragraph-small zocks-text-gray-600 mt-3'>
            A failed pull is recorded here and skipped for that run — it never withdraws the data source, because every account that enabled this app shares it.
          </p>
        </>
      ) : null}

      <RegisterDataSourceModal show={showRegister} clientId={clientId} onHide={() => setShowRegister(false)} />
      {testing.target ? (
        <TestDataSourceModal show={testing.open} clientId={clientId} dataSource={testing.target} onHide={testing.hide} onExited={testing.release} />
      ) : null}
      {editing.target ? (
        <EditDataSourceModal show={editing.open} clientId={clientId} dataSource={editing.target} onHide={editing.hide} onExited={editing.release} />
      ) : null}
      {deleting.target ? (
        <Modal
          show={deleting.open}
          onHide={deleting.hide}
          onExited={deleting.release}
          header='Delete data source'
          body={
            <p className='mb-0'>
              Delete &lsquo;{deleting.target.name}&rsquo;? Every account that enabled this app loses it immediately, and the signing secret stops working.
            </p>
          }
          showFooterActions
          footerPrimaryActionTitle='Delete'
          footerPrimaryActionButtonVariant='danger'
          footerSecondaryActionTitle='Cancel'
          onFooterPrimaryAction={handleConfirmDelete}
          onFooterSecondaryAction={deleting.hide}
          footerPrimaryActionLoading={isDeleting}
          footerPrimaryActionDisabled={isDeleting}
        />
      ) : null}
    </div>
  )
}
